import { ColDef } from 'ag-grid-community';
import { t } from 'i18next';

import { LinkCell } from 'core/elements';
import { LinkTarget } from 'core/enums';
import { NumberHelper } from 'core/helpers';
import { formatDate, getPercentage } from 'core/utils';
import { orderPartsPath } from 'orders/routes/routePaths';
import { IReusePriceConfig, ISalesHistoryItem } from 'parts/types';
import { PricingHelper } from 'pricing/helpers';
import { quotePartsPath } from 'quotes/routes/routePaths';

import { BillToAccountCell, LocationCell, PriceCell } from './cells';
import { SalesHistoryColId } from './enums';
import { getBillToAccountValue, getSalesLocationValue } from './utils';

interface IColumnDefOptions {
  reusePrice?: IReusePriceConfig;
}

export const columnDef = ({ reusePrice }: IColumnDefOptions = {}): ColDef[] => [
  {
    colId: SalesHistoryColId.REQUEST_DATE,
    headerName: t('headerRequested'),
    field: 'requestDate',
    filter: 'agDateColumnFilter',
    valueFormatter: ({ value }) => value && formatDate(value),
  },
  {
    colId: SalesHistoryColId.DOCUMENT_NUMBER,
    headerName: t('headerExternalOrderNumber'),
    field: 'documentNumber',
    filter: 'agTextColumnFilter',
    cellRenderer: LinkCell,
    cellRendererParams: {
      getPath: (data: ISalesHistoryItem) => data.externalOrderUrl,
      target: LinkTarget.BLANK,
      external: true,
      sx: { fontWeight: 600 },
    },
  },
  {
    colId: SalesHistoryColId.JOB_NAME,
    headerName: t('headerJobName'),
    field: 'jobName',
    filter: 'agTextColumnFilter',
    minWidth: 150,
  },
  {
    colId: SalesHistoryColId.ORDER_ID,
    headerName: t('headerOrderId'),
    field: 'orderId',
    filter: 'agNumberColumnFilter',
    cellRenderer: LinkCell,
    cellRendererParams: {
      columnId: SalesHistoryColId.ORDER_ID,
      // null = the history row matched no Partfiniti order; skip the path so LinkCell
      // renders the placeholder for the falsy value instead of a dead link.
      getPath: (data: ISalesHistoryItem) =>
        data.orderId ? orderPartsPath(data.orderId) : undefined,
      target: LinkTarget.BLANK,
    },
  },
  {
    colId: SalesHistoryColId.QUOTE_ID,
    headerName: t('headerQuoteId'),
    field: 'quoteId',
    filter: 'agNumberColumnFilter',
    cellRenderer: LinkCell,
    cellRendererParams: {
      columnId: SalesHistoryColId.QUOTE_ID,
      getPath: (data: ISalesHistoryItem) =>
        data.quoteId ? quotePartsPath(data.quoteId) : undefined,
      target: LinkTarget.BLANK,
    },
  },
  {
    colId: SalesHistoryColId.BILL_TO_ACCOUNT,
    headerName: t('headerBillToAccount'),
    field: 'billToAccountName',
    filter: 'agTextColumnFilter',
    filterValueGetter: ({ data }) => data && getBillToAccountValue(data),
    minWidth: 180,
    cellRenderer: BillToAccountCell,
  },
  {
    colId: SalesHistoryColId.SALES_LOCATION,
    headerName: t('headerSalesLocation'),
    field: 'salesLocation',
    filter: 'agTextColumnFilter',
    minWidth: 180,
    filterValueGetter: ({ data }) => data && getSalesLocationValue(data),
    cellRenderer: LocationCell,
  },
  {
    colId: SalesHistoryColId.QUANTITY,
    headerName: t('headerQuantity'),
    field: 'quantity',
    type: 'rightAligned',
    filter: 'agNumberColumnFilter',
    width: 100,
    valueFormatter: ({ data }) => data && NumberHelper.format({ amount: data?.quantity }),
  },
  {
    colId: SalesHistoryColId.UOM_CODE,
    headerName: t('headerUom'),
    field: 'uomCode',
    filter: 'agTextColumnFilter',
    width: 80,
    valueFormatter: ({ value }) => value?.toUpperCase(),
  },
  {
    colId: SalesHistoryColId.SELL_PRICE,
    headerName: t('headerSellPrice'),
    pinned: 'left',
    field: 'sellPrice',
    type: 'rightAligned',
    filter: 'agNumberColumnFilter',
    width: 120,
    cellRenderer: PriceCell,
    cellRendererParams: {
      reusePrice,
    },
  },
  {
    colId: SalesHistoryColId.MARGIN,
    headerName: t('headerMargin'),
    pinned: 'left',
    type: 'rightAligned',
    filter: 'agNumberColumnFilter',
    filterValueGetter: ({ data }) => {
      const gpm = PricingHelper.computeGpm(data?.standardCost, data?.sellPrice);

      return gpm != null ? gpm * 100 : null;
    },
    width: 100,
    valueGetter: ({ data }) => data && PricingHelper.computeGpm(data.standardCost, data.sellPrice),
    valueFormatter: ({ value }) => (value != null ? getPercentage(value) : ''),
  },
  {
    colId: SalesHistoryColId.STANDARD_COST,
    headerName: t('headerStandardCost'),
    field: 'standardCost',
    type: 'rightAligned',
    filter: 'agNumberColumnFilter',
    width: 120,
    valueFormatter: ({ data }) =>
      data?.standardCost != null ? PricingHelper.formatPricing({ amount: data.standardCost }) : '',
  },
  {
    colId: SalesHistoryColId.CUSTOMER_PO_NUMBER,
    headerName: t('headerPoNumber'),
    field: 'customerPoNumber',
    filter: 'agTextColumnFilter',
  },
  {
    colId: SalesHistoryColId.SHIP_TYPE,
    headerName: t('headerShipType'),
    field: 'shipType',
    filter: 'agTextColumnFilter',
    width: 100,
  },
  {
    colId: SalesHistoryColId.SHIP_FROM_LOCATION,
    headerName: t('headerShipFromLocation'),
    field: 'shipFromLocation',
    filter: 'agTextColumnFilter',
    width: 120,
  },
];
